import {
    ESpecialOperationType,
    ESpecialOperationTypeWithPage,
    ICommitSnapshot,
    ISelectedSnapshot,
    ISelectedWithCommitManager,
    ISlideType,
    ISpecialOperateExtInfo,
} from '@src/slide/application/service/selection/selected-with-commit-manager.interface';
import { IMasterLayoutEditService } from '@src/slide/application/service/master-layout-edit-service/master-layout-edit-service.interface';
import { ISelectionService } from '@src/slide/application/service/selection/selection-service.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { transaction } from 'mobx';
import { SelectedEleMgrType } from '@src/slide/domain/selection/constant/select-element-type';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import ElementBaseModel from '@src/slide/domain/ui-model/element-modeles/base-modeles/element-base-model';
import { ISlideDataModel, CommandSource, MutationType } from '@tencent/slide-sdk-model';
import { PageDataModel } from '@tencent/slide-sdk-model/src/model/page-data-model';
import { DataType } from '@tencent/slide-sdk-model/src/data/data';
import { EDIT_TYPE } from '@src/slide/domain/selection/constant/edit-type';
import { IPresentationModel } from '@src/slide/domain/ui-model/element-modeles/Interface/presentation-model.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { ITextSelectionUndoRedo } from '@src/slide/application/service/text-selection-undo-redo/interface';
import { TextMutations } from '@src/slide/application/service/text-selection-undo-redo/constant';

/**
 * 针对commit的变更来更新页面和元素选中态的manager
 * 注：
 * 每个commit都有一个创建前快照和创建后快照
 * undo是到对应commit创建前的快照，redo是到对应commit创建后的快照
 * 两个commit中间可能有各种选中态的切换
 * 此manager会将commit前后的选中快照存在一个map中，当undo/redo时取出对应的快照恢复选中态
 */
class SelectedWithCommitManager implements ISelectedWithCommitManager {
    // selection服务
    private readonly selectionService: ISelectionService;
    // masterLayout服务
    private masterLayoutEditService?: IMasterLayoutEditService;
    // commit和选中快照的绑定map
    private commitSelectedSnapshotMap: Map<string, ICommitSnapshot> = new Map();
    // 特殊操作（选中态比较特殊）
    private specialOperate: ESpecialOperationType | null = null;
    // 特殊操作的附加信息
    private specialOperateExtInfo: ISpecialOperateExtInfo | null = null;

    private textSelectionUndoRedo: ITextSelectionUndoRedo | null = null;

    public constructor(selection: ISelectionService) {
        this.selectionService = selection;
        // SlideApp.v3确保已被注入后，监听commit变更和失效事件
        SlideDataServices.SlideAppService.promise().then(() => {
            this.addCommitChangeListener();
            this.addCommandExpireListener();
        });
    }

    /**
     * 保存masterLayoutEditService，用来更新选中态时切换母版编辑页用
     * @param masterLayoutEditService
     */
    public setMasterLayoutEditServiceDelegate(masterLayoutEditService: IMasterLayoutEditService): void {
        if (this.masterLayoutEditService) {
            return;
        }
        this.masterLayoutEditService = masterLayoutEditService;
    }

    public setTextSelectionUndoRedoDelegate(textSelectionUndoRedo: ITextSelectionUndoRedo) {
        this.textSelectionUndoRedo = textSelectionUndoRedo;
    }

    /**
     * 标记特殊操作， 选中态的变更可根据标记自定义
     * @param operationType
     * @param operateExtInfo
     */
    public markSpecialOperate(
        operationType: ESpecialOperationType,
        operateExtInfo?: ISpecialOperateExtInfo,
    ): void {
        this.specialOperate = operationType;
        this.specialOperateExtInfo = operateExtInfo || null;
        // 避免某些mark后，但实际没执行commit操作的场景
        Promise.resolve().then(() => {
            this.clearSpecialOperate();
        });
    }

    private clearSpecialOperate(): void {
        this.specialOperate = null;
        this.specialOperateExtInfo = null;
    }

    /**
     * 监听commit变更通知
     *      sdk更新ui未更新时
     *          用户编辑： 存储commit和选中快照的绑定关系
     *      ui也更新后
     *          用户编辑/undo/redo：取出对应commit的快照更新到ui上
     * @private
     */
    private addCommitChangeListener(): void {
        SlideDataServices.SDKDataServices.EventManager()?.commandEvents
            .onUndoRedoChange(async ({ commandId, source, mutationsType }) => {
            // 业务侧有mark特殊操作，则优先采用，否则根据页和元素数量的变化推测当前的操作类型
                const operationType = this.specialOperate || this.guessOperationType();

                // 此通知是编辑产生的commit，在sdk数据更新后，uiModel更新前
                // 将此commit执行前后的选中态快照与此commitId绑定起来，存在commitSelectedInfoMap中
                // 当undo/redo时取出来选中快照直接设置在ui上，还原场景
                if ([CommandSource.Editor].includes(source)) {
                    this.bindSelectedWithCommit(commandId, operationType);
                }

                // 因为base-model的onDidChange通知晚于commandEvents的onUndoRedoChange，所以添加异步，确保uiModel已更新完
                // 再将此commit状态对应的选中快照更新到selection服务上
                if ([CommandSource.Editor, CommandSource.Undo, CommandSource.Redo].includes(source)) {
                    Promise.resolve().then(() => {
                        this.updateUiSelected(commandId, source, operationType);
                        this.updateTextSelection(commandId, source, mutationsType);
                    });
                }

                this.clearSpecialOperate();
            });
    }

    /**
     * 监听commit失效通知，从记录的map清掉记录
     * @private
     */
    private addCommandExpireListener(): void {
        SlideDataServices.SDKDataServices.EventManager()?.commandEvents.onClearCommand((taskIds) => {
            taskIds.forEach((e) => {
                this.commitSelectedSnapshotMap.delete(e);
            });
        });
    }

    /**
     * 绑定commit前后的选中快照和commitId的映射，存储在commitSelectedSnapshotMap中
     * @param commitId
     * @param operationType
     * @private
     */
    // eslint-disable-next-line complexity
    private bindSelectedWithCommit(commitId: string, operationType: ESpecialOperationType) {
        let afterSelectedSnapshot;
        let beforeSelectedSnapshot = this.getBeforeSelectedSnapshot();
        switch (operationType) {
            // 当前是特殊操作，说明commit后的选中数据与commit前不一致，要根据具体情况处理
            case ESpecialOperationType.addSlide:
            case ESpecialOperationType.addLayout:
            case ESpecialOperationType.addMaster:
                // 新增页，commit后应选中新增的页
                afterSelectedSnapshot = this.getAddSlideAfterSelectedSnapshot(operationType);
                break;
            case ESpecialOperationType.removeSlide:
            case ESpecialOperationType.removeLayout:
            case ESpecialOperationType.removeMaster:
                // 删除页，commit后应选中删除页后续页的第一个，没有则选中删除页上面页的第一个
                afterSelectedSnapshot = this.getRemoveSlideAfterSelectedSnapshot(operationType);
                break;
            case ESpecialOperationType.addElement:
                // 新增元素，commit后应选中新增的元素
                afterSelectedSnapshot = this.getAddElementAfterSelectedSnapshot();
                break;
            case ESpecialOperationType.removeElement:
                // 删除元素，commit后应取消选中删除的元素
                afterSelectedSnapshot = this.getRemoveElementAfterSelectedSnapshot();
                break;
            case ESpecialOperationType.textBlur:
                // 文本框失焦，commit前应记录失焦的元素
                beforeSelectedSnapshot = this.specialOperateExtInfo?.beforeSelectedSnapshot ?? beforeSelectedSnapshot;
                afterSelectedSnapshot = beforeSelectedSnapshot;
                break;
            default:
                // 当前不是特殊操作，则默认commit前和commit后的选中数据是一样的，都是当前状态
                afterSelectedSnapshot = beforeSelectedSnapshot;
        }
        // 将次绑定映射保存在map中
        this.commitSelectedSnapshotMap.set(commitId, { beforeSelectedSnapshot, afterSelectedSnapshot });
    }

    /**
     * 根据页面和元素数量的变化推测出当前是否为一些比较特殊的操作，比如新增页、删除页
     * 之所以称为特殊操作，是因为commit前后选择态是不一致的
     * 比如新增页commit后选中的是新加的页面，commit前是另一个页面
     * 而编辑页这种普通操作，commit前后选中的页面是一个，不变
     * @private
     */
    private guessOperationType(): ESpecialOperationType {
        const operationTypeWithPage = this.guessOperationWithPage();
        if (operationTypeWithPage) {
            return operationTypeWithPage;
        }
        const selectedSlide = this.getSelectedSlideList();
        if (selectedSlide.length === 1) {
            // 页的数量没变，且只选中了一页，那应该就是编辑当前页
            const { uiElementIdArr, sdkElementIdArr } = this.getAfterCommitElementIdArr();
            if (sdkElementIdArr.length > uiElementIdArr.length) {
                // 元素变多了，肯定是新增元素
                return ESpecialOperationType.addElement;
            }
            for (let i = 0; i < sdkElementIdArr.length; i++) {
                if (!uiElementIdArr.includes(sdkElementIdArr[i])) {
                    // 元素没变多，但是出现了新的元素，那可能是成组的原因，也算是新增元素
                    return ESpecialOperationType.addElement;
                }
            }
            if (sdkElementIdArr.length < uiElementIdArr.length) {
                // 其余场景，元素变少了，那就是删除元素
                return ESpecialOperationType.removeElement;
            }
        }
        return ESpecialOperationType.keep;
    }

    /**
     * 获取commit前页面和元素的选中快照
     * @private
     */
    private getBeforeSelectedSnapshot(): ISelectedSnapshot {
        return {
            currentSlideType: this.selectionService.currentSlideType,
            slideIdArr: this.getSelectedSlideList().map(e => e?.id || '')
                .filter(e => !!e) || [],
            slideElementIdArr: this.selectionService.selectedElements
                .selectedArrayForType(SelectedEleMgrType.SLIDE)
                .map(e => e?.id || '')
                .filter(e => !!e) || [],
            groupElementIdArr: this.selectionService.selectedElements
                .selectedArrayForType(SelectedEleMgrType.GROUP)
                .map(e => ({ rootId: e.rootGroupModel?.id || '', childId: e.id || '' })) || [],
        };
    }

    /**
     * 获取新增页后的选中快照
     * 选中的是新增的页
     * 元素均不选中
     * @private
     */
    private getAddSlideAfterSelectedSnapshot(operationType: ESpecialOperationTypeWithPage): ISelectedSnapshot {
        const { uiSlideIdArr, sdkSlideIdArr } = this.getAfterCommitSlideIdArr(operationType);
        return {
            currentSlideType: this.selectionService.currentSlideType,
            slideIdArr: sdkSlideIdArr.filter(e => !uiSlideIdArr.includes(e)),
            slideElementIdArr: [],
            groupElementIdArr: [],
        };
    }

    /**
     * 获取删除页后的选中快照
     * 选中页是删除页后面页的第一个，没有则是删除页前面页最近的那个
     * 元素均不选中
     * @private
     */
    private getRemoveSlideAfterSelectedSnapshot(operationType: ESpecialOperationTypeWithPage): ISelectedSnapshot {
        const { uiSlideIdArr, sdkSlideIdArr } = this.getAfterCommitSlideIdArr(operationType);
        // 最后一个删除页的索引，因为可能一次删多页
        const lastSelectedSlide = this.getSelectedSlideList().slice(-1)[0];
        const lastSelectedSlideIndex = uiSlideIdArr.findIndex(e => e === lastSelectedSlide?.id) || 0;
        const beforeSlideIdArr = uiSlideIdArr.slice(0, lastSelectedSlideIndex + 1);
        const afterSlideIdArr = uiSlideIdArr.slice(lastSelectedSlideIndex + 1);
        let newSelectSlideId;
        // 最后一个删除页后面如果还有页的话，则选中第一个
        newSelectSlideId = sdkSlideIdArr.find(e => afterSlideIdArr.includes(e));
        if (!newSelectSlideId) {
            // 最后一个删除页后面没页了，那就倒序找前面的第一个
            newSelectSlideId = sdkSlideIdArr.concat().reverse()
                .find(e => beforeSlideIdArr.includes(e));
        }
        return {
            currentSlideType: this.selectionService.currentSlideType,
            slideIdArr: newSelectSlideId ? [newSelectSlideId] : [],
            slideElementIdArr: [],
            groupElementIdArr: [],
        };
    }

    /**
     * 获取新增元素后的选中快照
     * 选中的页不变
     * 选中的元素是新增的元素
     * @private
     */
    private getAddElementAfterSelectedSnapshot(): ISelectedSnapshot {
        const { uiElementIdArr, sdkElementIdArr } = this.getAfterCommitElementIdArr();
        return {
            currentSlideType: this.selectionService.currentSlideType,
            slideIdArr: this.getSelectedSlideList().map(e => e?.id || '')
                .filter(e => !!e) || [],
            slideElementIdArr: sdkElementIdArr.filter(e => !uiElementIdArr.includes(e)),
            groupElementIdArr: [],
        };
    }

    /**
     * 获取删除元素后的选中信息
     * 选中的页不变
     * 元素均不选中
     * @private
     */
    private getRemoveElementAfterSelectedSnapshot(): ISelectedSnapshot {
        const slideUiElementIdArr = this.selectionService?.selectedElements?.selectedArray
            ?.map(e => e?.id || '')?.filter(e => !!e) || [];
        const { sdkElementIdArr } = this.getAfterCommitElementIdArr();
        return {
            currentSlideType: this.selectionService.currentSlideType,
            slideIdArr: this.getSelectedSlideList().map(e => e?.id || '')
                .filter(e => !!e) || [],
            slideElementIdArr: slideUiElementIdArr.filter(e => sdkElementIdArr.includes(e)), // 当前页面选中元素中剔除已被删掉的
            groupElementIdArr: [],
        };
    }

    /**
     * 获取commit更新后UI未更新前的sdk和ui的slideList
     * @private
     */
    private getAfterCommitSlideIdArr(operationType: ESpecialOperationTypeWithPage): {
        uiSlideIdArr: string[];
        sdkSlideIdArr: string[];
    } {
        const pageType = this.getPageTypeAfterCommit(operationType) ?? this.selectionService.currentSlideType;
        const { uiSlideList, sdkSlideList } = this.getSlideList(pageType);
        const uiSlideIdArr = uiSlideList.map(e => e.id || '').filter(e => !!e);
        const sdkSlideIdArr = sdkSlideList.map(e => e.id || '').filter(e => !!e);
        return { uiSlideIdArr, sdkSlideIdArr };
    }

    /**
     * 猜测commit更新是什么页操作
     * 若猜测不是页操作，返回 null
     * @private
     */
    private guessOperationWithPage(): ESpecialOperationTypeWithPage | null {
        const { uiSlideList, sdkSlideList } = this.getSlideList(UIModelType.SLIDE);
        if (uiSlideList.length < sdkSlideList.length) {
            return ESpecialOperationType.addSlide;
        }
        if (uiSlideList.length > sdkSlideList.length) {
            return ESpecialOperationType.removeSlide;
        }

        if (this.masterLayoutEditService?.editType === EDIT_TYPE.MASTER_LAYOUT) {
            const { uiSlideList: uiLayoutList, sdkSlideList: sdkLayoutList } = this.getSlideList(UIModelType.LAYOUT);
            if (uiLayoutList.length < sdkLayoutList.length) {
                return ESpecialOperationType.addLayout;
            }
            if (uiLayoutList.length > sdkLayoutList.length) {
                return ESpecialOperationType.removeLayout;
            }

            const { uiSlideList: uiMasterList, sdkSlideList: sdkMasterList } = this.getSlideList(UIModelType.MASTER);
            if (uiMasterList.length < sdkMasterList.length) {
                return ESpecialOperationType.addMaster;
            }
            if (uiMasterList.length > sdkMasterList.length) {
                return ESpecialOperationType.removeMaster;
            }
        }

        return null;
    }

    /**
     * 获取commit更新后UI未更新前的sdk和ui的ElementList
     * @private
     */
    private getAfterCommitElementIdArr(): {
        uiElementIdArr: string[],
        uiGroupElementIdArr: string[],
        sdkElementIdArr: string[],
        sdkGroupElementIdArr: string[],
    } {
        const { currentSlide } = this.selectionService;
        const uiElementIdArr = currentSlide?.children?.map(e => e.id || '').filter(e => !!e) || [];
        const uiGroupElementIdArr = currentSlide?.children?.filter(e => e.type === UIModelType.GROUP)?.map(e => e.id || '')
            .filter(e => !!e) || [];
        const slideSdkData = currentSlide?.sdkData as unknown as PageDataModel;
        const sdkElementIdArr = slideSdkData?.shapeList?.map(e => e.id || '').filter(e => !!e) || [];
        const sdkGroupElementIdArr = slideSdkData?.shapeList?.filter(e => e.type === DataType.GroupShape)?.map(e => e.id || '')
            .filter(e => !!e) || [];
        return { uiElementIdArr, uiGroupElementIdArr, sdkElementIdArr, sdkGroupElementIdArr };
    }

    /**
     * 将选中快照更新到UI上
     * @param commitId 提交ID
     * @param source 提交类型
     * @param operationType 猜测的用户操作
     * @private
     */
    private updateUiSelected(commitId: string, source: CommandSource, operationType: ESpecialOperationType): void {
        const commitSnapshot = this.commitSelectedSnapshotMap.get(commitId);
        if (!commitSnapshot) {
            return;
        }

        let selectedSnapshot = commitSnapshot.afterSelectedSnapshot;
        if (source === CommandSource.Undo) {
            // undo 是当前commit创建前的快照
            selectedSnapshot = commitSnapshot.beforeSelectedSnapshot;
        } else if (source === CommandSource.Redo) {
            // redo 是当前commit创建后的快照
            selectedSnapshot = commitSnapshot.afterSelectedSnapshot;
        } else if (source === CommandSource.Editor) {
            // 编辑 是当前commit创建后的快照
            selectedSnapshot = commitSnapshot.afterSelectedSnapshot;
        }

        // 如果是用户的普通编辑，理论上当前的选中态不变，所以不执行下面的重置选中态
        // 重置的话反而会引发其他问题，比如两个文本框编辑一个然后点击另一个，前一个失焦保存，如果重置，会把选择框重置回保存的那个上面
        if (
            (source === CommandSource.Editor)
            && ([ESpecialOperationType.keep, ESpecialOperationType.textBlur].includes(operationType))
        ) {
            return;
        }

        // 将此选中态更新到ui上
        transaction(() => {
            const { currentSlideType, slideIdArr, slideElementIdArr, groupElementIdArr } = selectedSnapshot;
            const oldCurrentSlideId = this.selectionService.currentSlide?.id;
            // （1）当前编辑类型
            const currentPageType = this.getPageTypeAfterCommit(operationType) ?? currentSlideType;
            const editType = currentPageType === UIModelType.SLIDE ? EDIT_TYPE.SLIDE : EDIT_TYPE.MASTER_LAYOUT;
            this.selectionService.updateCurrentSlideType(currentPageType);
            this.masterLayoutEditService?.updateEditType(editType);
            // （2）选中页
            const slideList = this.getSlideList(currentPageType).uiSlideList;
            const selectedSlideList = slideList.filter(e => slideIdArr.includes(e.id || ''));
            this.selectionService.selectedSlides.clear();
            this.selectionService.selectedSlides.add(...selectedSlideList);
            if (selectedSlideList.length) {
                this.selectionService.
                    currentSlideIndex = slideList.findIndex(e => e.id === selectedSlideList[0].id);
            }
            if (oldCurrentSlideId !== this.selectionService.currentSlide?.id) {
                // 切换当前页会有一个reaction延迟清掉选中元素，因为下面已经做了这个工作，所以禁止掉这个reaction，防止刚被赋值的被清掉
                this.selectionService.preventSelectedElementsClear = true;
            }
            // （3）选中元素
            const [currentSlide] = selectedSlideList;
            // slide子元素
            const slideElementArr = currentSlide?.children
                .filter(e => slideElementIdArr.includes(e.id || '') && e.selectable) || [];
            // 组合子元素
            const groupElementArr: ElementBaseModel[] = [];
            const groupChildrenMap: { [key: string]: ElementBaseModel[] } = {};
            groupElementIdArr.forEach((e) => {
                if (!e.rootId) {
                    return;
                }
                if (!groupChildrenMap[e.rootId]) {
                    const routGroup = currentSlide?.children.find(child => child.id === e.rootId);
                    groupChildrenMap[e.rootId] = this.flatGroupChildren(routGroup, []);
                }
                const childElement = groupChildrenMap[e.rootId].find(child => child.id === e.childId);
                if (childElement?.selectable) {
                    groupElementArr.push(childElement);
                }
            });

            // 清除失效的选中元素，加入新增的选中元素
            const selectedElementsManager = this.selectionService.selectedElements;
            const newSelectedElements = [...slideElementArr, ...groupElementArr];
            const oldSelectedElements = [...selectedElementsManager.selectedArrayForType(SelectedEleMgrType.SLIDE),
                ...selectedElementsManager.selectedArrayForType(SelectedEleMgrType.GROUP)];
            const invalidElements = oldSelectedElements.filter(e => !newSelectedElements.includes(e));
            selectedElementsManager.remove(...invalidElements);
            selectedElementsManager.add(...newSelectedElements);
        });
    }

    /**
     * 在触发文本相关的 undo redo 时还原文本的选区，需要在更新完元素的选中状态之后执行
     * @param commitId 提交ID
     * @param source 提交类型
     */
    private updateTextSelection(commandId: string, source: CommandSource, mutationsType: MutationType[]) {
        if (!this.textSelectionUndoRedo) {
            return;
        }
        if (!(source === CommandSource.Undo || source === CommandSource.Redo)) {
            // 当前操作不是 undo redo，直接返回
            return;
        }
        if (!mutationsType.some(type => TextMutations.includes(type))) {
            // 当前操作和文本无关，直接返回
            return;
        }
        const { selectedElements } = this.selectionService;
        // 获取当前选中的 model，如果没选中的，代表当前操作区域是在备注，直接使用 slideModel
        const currentModel = selectedElements.length === 0
            ? this.selectionService.currentSlide
            : selectedElements.getCurrentModel();
        this.textSelectionUndoRedo.triggerUndoRedo?.(source, commandId, currentModel);
    }

    /**
     * 获取Presentation UiModel
     * @private
     */
    private getPresentation(): IPresentationModel {
        return this.selectionService.uiDataService.presentation;
    }

    /**
     * 根据当前编辑类型获取全量slideList/masterList/layoutList
     * @param slideType
     * @private
     */
    private getSlideList(slideType: ISlideType): {
        uiSlideList: ISlideModel[], sdkSlideList: ISlideDataModel[],
    } {
        let uiSlideList;
        let sdkSlideList;
        if (slideType === UIModelType.SLIDE) {
            uiSlideList = this.getPresentation().children;
            sdkSlideList = this.getPresentation().sdkData?.slideList;
        } else if (slideType === UIModelType.MASTER) {
            uiSlideList = this.getPresentation().masterList;
            sdkSlideList = this.getPresentation().sdkData?.masterList;
        } else if (slideType === UIModelType.LAYOUT) {
            uiSlideList = this.getPresentation().layoutList;
            sdkSlideList = this.getPresentation().sdkData?.layoutList;
        }
        return { uiSlideList, sdkSlideList };
    }

    /**
     * 获取当前选中的slideList
     * @private
     */
    private getSelectedSlideList(): ISlideModel[] {
        let { selectedArray } = this.selectionService.selectedSlides;
        if (!selectedArray.length && this.selectionService.currentSlide) {
            selectedArray = [this.selectionService.currentSlide];
        }
        return selectedArray;
    }

    /**
     * 将某个组合的所有层级子元素打平放在一个数组中，便于根据子元素ID找到Model
     * @param group
     * @param arr
     * @private
     */
    private flatGroupChildren(group, arr: ElementBaseModel[] = []): ElementBaseModel[] {
        (group?.children || []).forEach((e) => {
            arr.push(e);
            if (e.children) {
                this.flatGroupChildren(e, arr);
            }
        });
        return arr;
    }

    private getPageTypeAfterCommit(operationType: ESpecialOperationType): ISlideType | undefined {
        return {
            [ESpecialOperationType.addSlide]: UIModelType.SLIDE,
            [ESpecialOperationType.removeSlide]: UIModelType.SLIDE,
            [ESpecialOperationType.addLayout]: UIModelType.LAYOUT,
            [ESpecialOperationType.removeLayout]: UIModelType.LAYOUT,
            [ESpecialOperationType.addMaster]: UIModelType.MASTER,
            [ESpecialOperationType.removeMaster]: UIModelType.MASTER,
        }[operationType];
    }
}

export default SelectedWithCommitManager;
